import {WidgetData} from '../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../core/shared/widget/widget-data-param';
import {FilterCriteria} from '../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../core/shared/misc/sorting';
import {QlQueryDto} from '../../../core/shared/ql/data/dto/ql-query.dto';
import {buildCwCuratedWidgetForm} from './cw-widget-form.util';

const WIDGET_ALIAS = 'config.cw.CwCaseTypeExchangeDefinition.widget';

/**
 * Builds the "Austausch-Definitionen" config table widget for cw.CwCaseTypeExchangeDefinition
 * (design G.5 node id `exchangeDefinitions`) — one row per (CaseType × operation), the operation
 * anchor the Assembler resolves for a given BiPRO call. Read-mostly inspection table: rows come from
 * the SMART-Seed bundle, never hand-added in the UI (wp4-fe-ruckfragen-resolved.md Q7).
 *
 * CW-EXCHANGE-V2 (design §3.1) slimmed the entity down to the transport metadata plus an on/off
 * switch per operation: `structureVariant` is gone (it now reaches the template as
 * `typeConfig.structureVariant` from the product's BiPRO coordinate), the skeleton FK is gone (there
 * is ONE skeleton per CaseType), and so are the MTOM flags (transport detects them).
 *
 * WidgetFactory.createWidgetTableQl on an explicit, hand-curated QlQueryDto (root
 * cw.CwCaseTypeExchangeDefinition aliased `e`, one LEFT join to the owning caseType selecting only
 * its display NAME) plus a curated WidgetForm for explicit German column headers.
 *
 * @param caseTypeId optional cw.CwCaseType id to scope the table to one CaseType (e.g. embedded
 *   under that CaseType's node/detail); omit for the unscoped, all-CaseTypes inspection table.
 */
export function buildCwCaseTypeExchangeDefinitionTableWidget(caseTypeId?: number): WidgetData {
    const qlQuery: QlQueryDto = {
        name: WIDGET_ALIAS,
        type: 'view',
        start: {
            name: 'cw.CwCaseTypeExchangeDefinition',
            as: 'e',
            type: 'entity',
            fields: [
                {name: 'id'},
                {name: 'operationKey'}, // Operationsschlüssel
                {name: 'soapAction'},   // SOAP-Action
                {name: 'validateXsd'},  // XSD validieren
                {name: 'active'},       // Aktiv
            ],
            joins: [
                {
                    type: 'entity', name: 'caseType', as: 'ct', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}], // Vorgangstyp (resolved NAME)
                },
            ],
        },
        fixedFilters: caseTypeId != null
            ? [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, caseTypeId)]
            : [],
        sortings: [new Sorting('e.operationKey', true)],
    };

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: CwCaseTypeExchangeDefinition config table — the per-CaseType operation anchor, curated
     *   columns (Vorgangstyp, Operationsschlüssel, SOAP-Action, XSD validieren, Aktiv).
     */
    const widgetData = WidgetFactory.createWidgetTableQl(
        WIDGET_ALIAS,
        'Austausch-Definitionen',
        qlQuery,
        WidgetDataParam.create('widgetForm', buildCwCaseTypeExchangeDefinitionWidgetForm()),
    );
    widgetData.noDataText = 'Keine Austausch-Definitionen konfiguriert';
    return widgetData;
}

function buildCwCaseTypeExchangeDefinitionWidgetForm() {
    return buildCwCuratedWidgetForm([
        {field: 'ct.name', uiLabel: 'Vorgangstyp'},
        {field: 'e.operationKey', uiLabel: 'Operationsschlüssel'},
        {field: 'e.soapAction', uiLabel: 'SOAP-Action'},
        {field: 'e.validateXsd', uiLabel: 'XSD validieren'},
        {field: 'e.active', uiLabel: 'Aktiv'},
    ]);
}
